import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Select, Switch } from '@headlessui/react'
import {
    PencilIcon,
    ClipboardIcon,
    TrashIcon,
    EyeIcon,
    UserPlusIcon,
    ChevronUpDownIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/solid';

// ============================================
// HOOK PARA SORTING
// ============================================

/**
 * Hook para manejar sorting de datos
 * @param {Array} data - Datos a ordenar
 * @param {Object} config - Configuración inicial { field, direction }
 * @returns {Object} { sortedData, sortConfig, handleSort }
 */
export const useTableSort = (data, config = { field: '', direction: 'ASC' }) => {
    const [sortConfig, setSortConfig] = useState(config);

    const sortedData = useMemo(() => {
        if (!sortConfig.field || !data) return data;

        const sorted = [...data].sort((a, b) => {
            // Soporta campos anidados como 'doctor.apellidos'
            const getValue = (obj, path) => {
                return path.split('.').reduce((current, key) => current?.[key], obj);
            };

            const aValue = getValue(a, sortConfig.field);
            const bValue = getValue(b, sortConfig.field);

            // Manejar nulls/undefined
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Comparar strings sin case sensitivity
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
                return sortConfig.direction === 'ASC' ? comparison : -comparison;
            }

            // Comparar números y otros tipos
            if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [data, sortConfig]);

    const handleSort = (field) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'ASC' ? 'DESC' : 'ASC'
        }));
    };

    return { sortedData, sortConfig, handleSort, setSortConfig };
};

/**
 * Hook para manejar paginación de datos
 * @param {Array} data - Datos a paginar
 * @param {Object} config - Configuración inicial { page, itemsPerPage }
 * @returns {Object} { paginatedData, page, itemsPerPage, totalItems, totalPages, ... }
 */
export const useTablePagination = (data, config = { page: 1, itemsPerPage: 10 }) => {
    const [page, setPage] = useState(config.page);
    const [itemsPerPage, setItemsPerPage] = useState(config.itemsPerPage);

    const totalItems = data?.length || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Resetear a página 1 si los datos cambian y la página actual es inválida
    useMemo(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(1);
        }
    }, [totalItems, itemsPerPage]);

    const paginatedData = useMemo(() => {
        if (!data) return [];
        const start = (page - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return data.slice(start, end);
    }, [data, page, itemsPerPage]);

    const goToPage = (newPage) => {
        const validPage = Math.max(1, Math.min(newPage, totalPages));
        setPage(validPage);
    };

    const nextPage = () => goToPage(page + 1);
    const prevPage = () => goToPage(page - 1);
    const firstPage = () => goToPage(1);
    const lastPage = () => goToPage(totalPages);

    return {
        paginatedData,
        page,
        setPage: goToPage,
        itemsPerPage,
        setItemsPerPage: (newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setPage(1); // Reset a página 1 al cambiar items por página
        },
        totalItems,
        totalPages,
        nextPage,
        prevPage,
        firstPage,
        lastPage,
        hasNextPage: page < totalPages,
        hasPrevPage: page > 1,
    };
};

// ============================================
// COMPONENTES AUXILIARES
// ============================================

/**
 * Avatar para tablas con fallback a iniciales
 */
export const TableAvatar = ({ 
    src, 
    alt = '', 
    name = '',
    size = 'md',
    className = '' 
}) => {
    const sizes = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
    };
    
    const initials = name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    if (src) {
        return (
            <img 
                src={src} 
                alt={alt || name}
                className={`${sizes[size]} rounded-full object-cover ${className} sombra-azul-chica`}
                onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList.remove('hidden');
                }}
            />
        );
    }

    return (
        <div className={`
            ${sizes[size]} 
            rounded-full 
            bg-linear-to-br from-[#0033ff] to-[#11cdef]
            text-white 
            flex items-center justify-center 
            font-medium
            sombra-azul-chica
            ${className}
        `}>
            {initials || '?'}
        </div>
    );
};

/**
 * Badge de estado para tablas
 */
export const TableBadge = ({ 
    status, 
    activeText = 'Activo', 
    inactiveText = 'Inactivo',
    activeColor = 'bg-green-100 text-green-800',
    inactiveColor = 'bg-red-100 text-red-800',
    className = ''
}) => {
    const isActive = status === true || status === 1 || status === 'active' || status === 'Activo';
    
    return (
        <span className={`
            inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
            ${isActive ? activeColor : inactiveColor}
            ${className}
        `}>
            {isActive ? activeText : inactiveText}
        </span>
    );
};

/**
 * Switch de activación/desactivación para tablas
 * @param {boolean|number} value - Valor actual (true/false, 1/0)
 * @param {function} onChange - Callback cuando cambia el estado, recibe (newValue, row)
 * @param {object} row - Fila de datos (opcional, se pasa al onChange)
 * @param {boolean} disabled - Deshabilitar el switch
 * @param {boolean} loading - Mostrar estado de carga
 * @param {string} activeColor - Color cuando activo: clase Tailwind ('bg-blue-600') o hex ('#3b82f6')
 * @param {string} inactiveColor - Color cuando inactivo: clase Tailwind ('bg-gray-300') o hex ('#d1d5db')
 * @param {string} size - Tamaño: 'sm', 'md', 'lg'
 * @param {string} label - Etiqueta opcional
 * @param {string} className - Clases adicionales
 */
export const TableSwitch = ({ 
    value, 
    onChange,
    row = null,
    disabled = false,
    loading = false,
    activeColor = 'bg-blue-600',
    inactiveColor = 'bg-gray-300',
    size = 'sm',
    label = '',
    className = ''
}) => {
    const isActive = value === true || value === 1 || value === '1' || value === 'active' || value === 'Activo';
    
    // Detectar si es color hexadecimal (empieza con #)
    const isHexColor = (color) => typeof color === 'string' && color.startsWith('#');
    const activeIsHex = isHexColor(activeColor);
    const inactiveIsHex = isHexColor(inactiveColor);
  
    // Determinar clases y estilos
    const colorClass = isActive 
        ? (activeIsHex ? '' : activeColor)
        : (inactiveIsHex ? '' : inactiveColor);
    
    const colorStyle = isActive
        ? (activeIsHex ? { backgroundColor: activeColor } : {})
        : (inactiveIsHex ? { backgroundColor: inactiveColor } : {});

    // Tamaños del switch
    const sizes = {
        sm: {
            track: 'h-5 w-9',
            thumb: 'h-3.5 w-3.5',
            translate: 'translate-x-4',
            translateOff: 'translate-x-0.5'
        },
        md: {
            track: 'h-6 w-11',
            thumb: 'h-4 w-4',
            translate: 'translate-x-5',
            translateOff: 'translate-x-0.5'
        },
        lg: {
            track: 'h-7 w-14',
            thumb: 'h-5 w-5',
            translate: 'translate-x-7',
            translateOff: 'translate-x-1'
        }
    };

    const sizeConfig = sizes[size] || sizes.sm;

    const handleChange = (newValue) => {
        if (!disabled && !loading && onChange) {
            onChange(newValue, row);
        }
    };

    return (
        <div className={`flex items-center justify-center gap-2 ${className}`}>
            {label && <span className="text-sm text-gray-600">{label}</span>}
            <Switch
                checked={isActive}
                onChange={handleChange}
                disabled={disabled || loading}
                style={colorStyle}
                className={`
                    ${colorClass}
                    relative inline-flex ${sizeConfig.track} items-center rounded-full 
                    transition-colors duration-200 ease-in-out
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                    ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
            >
                <span className="sr-only">{label || 'Toggle'}</span>
                <span
                    className={`
                        ${isActive ? sizeConfig.translate : sizeConfig.translateOff}
                        inline-flex ${sizeConfig.thumb} items-center justify-center transform rounded-full bg-white shadow-md
                        transition-transform duration-200 ease-in-out
                    `}
                >
                    {loading && (
                        <svg 
                            className="animate-spin h-2.5 w-2.5 text-gray-400" 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24"
                        >
                            <circle 
                                className="opacity-25" 
                                cx="12" 
                                cy="12" 
                                r="10" 
                                stroke="currentColor" 
                                strokeWidth="4"
                            />
                            <path 
                                className="opacity-75" 
                                fill="currentColor" 
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                    )}
                </span>
            </Switch>
        </div>
    );
};

/**
 * Botón de acción para la tabla
 */
const ActionButton = ({ action, row, permissions }) => {
    // Verificar permiso si existe
    if (action.permission && permissions && !permissions[action.permission]) {
        return null;
    }

    // Verificar condición personalizada
    if (action.condition && !action.condition(row)) {
        return null;
    }

    const icons = {
        edit: PencilIcon,
        view: ClipboardIcon,
        delete: TrashIcon,
        eye: EyeIcon,
        addUser: UserPlusIcon,
    };

    const colors = {
        edit: 'text-green-600 hover:text-green-800 hover:bg-green-50',
        view: 'text-[#3c4d69] hover:text-[#2a3a5a] hover:bg-[#e8f0fe]',
        delete: 'text-red-600 hover:text-red-800 hover:bg-red-50',
        eye: 'text-gray-600 hover:text-gray-800 hover:bg-gray-50',
        addUser: 'text-blue-600 hover:text-blue-800 hover:bg-blue-50',
        default: 'text-gray-600 hover:text-gray-800 hover:bg-gray-50',
    };

    const IconComponent = action.icon 
        ? (typeof action.icon === 'string' ? icons[action.icon] : action.icon)
        : icons[action.type] || icons.edit;
    
    const colorClass = colors[action.type] || colors.default;

    const buttonContent = (
        <>
            {IconComponent && <IconComponent className="w-5 h-5 cursor-pointer" />}
            {action.label && <span className="ml-1 text-sm">{action.label}</span>}
        </>
    );

    const baseClasses = `
        inline-flex items-center justify-center p-1.5 rounded-md
        transition-colors duration-150
        ${colorClass}
        ${action.className || ''}
    `;

    // Si es un Link
    if (action.to) {
        const path = typeof action.to === 'function' ? action.to(row) : action.to;
        return (
            <Link to={path} className={baseClasses} title={action.tooltip}>
                {buttonContent}
            </Link>
        );
    }

    // Si es un botón con onClick
    return (
        <button 
            type="button"
            onClick={() => action.onClick?.(row)}
            className={baseClasses}
            title={action.tooltip}
        >
            {buttonContent}
        </button>
    );
};

/**
 * Icono de ordenamiento
 */
const SortIcon = ({ field, sortConfig }) => {
    if (!sortConfig || sortConfig.field !== field) {
        return <ChevronUpDownIcon className="w-4 h-4 ml-1 text-gray-400" />;
    }
    
    return sortConfig.direction === 'ASC' 
        ? <ChevronUpIcon className="w-4 h-4 ml-1 text-blue-600" />
        : <ChevronDownIcon className="w-4 h-4 ml-1 text-blue-600" />;
};

/**
 * Componente de paginación
 */
const TablePagination = ({
    currentPage,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    itemsPerPageOptions = [5, 10, 25, 50, 100],
}) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    // Generar números de página
    const getPageNumbers = () => {
        const pages = [];
        const showPages = 5; // Máximo de páginas a mostrar
        
        let start = Math.max(1, currentPage - Math.floor(showPages / 2));
        let end = Math.min(totalPages, start + showPages - 1);
        
        if (end - start + 1 < showPages) {
            start = Math.max(1, end - showPages + 1);
        }
        
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        
        return pages;
    };

    if (totalPages <= 1 && !onItemsPerPageChange) return null;

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 border-t border-gray-200">
            {/* Items per page */}
            {onItemsPerPageChange && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Mostrar</span>
                    <Select
                        value={itemsPerPage}
                        onChange={(e) => {
                            onItemsPerPageChange(Number(e.target.value));
                            onPageChange(1);
                        }}
                        className="border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 browser-default"
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                        <option value={totalItems}>Todos</option>
                    </Select>
                    <span>por página</span>
                </div>
            )}

            {/* Page numbers */}
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </button>

                    {getPageNumbers().map(page => (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`
                                min-w-8 h-8 px-2 rounded-md text-sm font-medium
                                ${page === currentPage 
                                    ? 'bg-[#0033ff] text-white' 
                                    : 'hover:bg-gray-200 text-gray-700'
                                }
                            `}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronRightIcon className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Info */}
            <div className="text-sm text-gray-600">
                {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems}
            </div>
        </div>
    );
};

// ============================================
// COMPONENTE PRINCIPAL TABLE
// ============================================

/**
 * Componente Table reutilizable con Tailwind CSS
 * 
 * @param {Array} columns - Configuración de columnas
 * @param {Array} data - Datos a mostrar
 * @param {Array} actions - Acciones por fila (edit, view, delete, custom)
 * @param {Object} permissions - Permisos del usuario
 * @param {Object} sorting - { field, direction, onSort } para ordenamiento
 * @param {Object} pagination - { page, itemsPerPage, totalItems, onPageChange, onItemsPerPageChange }
 * @param {Function} keyExtractor - Función para extraer key única de cada fila
 * @param {boolean} loading - Estado de carga
 * @param {boolean} striped - Filas con colores alternados
 * @param {boolean} hoverable - Efecto hover en filas
 * @param {string} emptyMessage - Mensaje cuando no hay datos
 * @param {React.Component} emptyComponent - Componente personalizado para estado vacío
 * @param {React.Component} loadingComponent - Componente personalizado para carga
 */
const Table = ({
    columns = [],
    data = [],
    actions = [],
    permissions = {},
    sorting = null,
    pagination = null,
    keyExtractor = (row) => row.id,
    loading = false,
    striped = true,
    hoverable = true,
    bordered = false,
    rounded = true,
    shadow = true,
    responsive = true,
    mobileBreakpoint = 'md',
    emptyMessage = 'No hay datos disponibles',
    emptyComponent = null,
    loadingComponent = null,
    className = '',
    headerClassName = '',
    bodyClassName = '',
    containerClassName = '',
    rowClassName = null,
}) => {
    // Combinar columnas con acciones si existen
    const allColumns = useMemo(() => {
        if (actions.length === 0) return columns;
        
        // Filtrar acciones basadas en permisos globales
        const visibleActions = actions.filter(action => {
            if (!action.permission) return true;
            return permissions[action.permission];
        });

        if (visibleActions.length === 0) return columns;

        return [
            ...columns,
            {
                key: '_actions',
                label: 'Acciones',
                align: 'center',
                width: actions.length <= 2 ? '80px' : `${actions.length * 40}px`,
            }
        ];
    }, [columns, actions, permissions]);

    // Clases del contenedor
    const containerClasses = [
        'w-full overflow-hidden',
        rounded && 'rounded-lg',
        shadow && 'shadow-md',
        bordered && 'border border-gray-200',
        containerClassName,
    ].filter(Boolean).join(' ');

    // Clases de la tabla
    const tableClasses = [
        'w-full',
        className,
    ].filter(Boolean).join(' ');

    // Clases del header
    const headerClasses = [
        'bg-gray-100 text-gray-700 text-sm uppercase tracking-wider',
        headerClassName,
    ].filter(Boolean).join(' ');

    // Clases de las celdas
    const cellClasses = 'px-4 py-3 text-sm';

    // Función para obtener clases de fila
    const getRowClasses = (row, index) => {
        const classes = ['border-b border-gray-200'];
        if (rowClassName) {
            const custom = rowClassName(row, index);
            if (custom) {
                classes.push(custom);
                return classes.join(' ');
            }
        }
        if (striped && index % 2 === 1) classes.push('bg-[#e8f0fe]');
        if (hoverable) classes.push('hover:bg-blue-50 transition-colors');
        return classes.join(' ');
    };

    // Obtener valor de campo anidado (ej: 'doctor.apellidos')
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    };

    // Estado de carga
    if (loading) {
        return (
            <div className={containerClasses}>
                {loadingComponent || (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033ff]"></div>
                        <span className="ml-3 text-gray-600">Cargando...</span>
                    </div>
                )}
            </div>
        );
    }

    // Estado vacío
    if (!data || data.length === 0) {
        return (
            <div className={containerClasses}>
                {emptyComponent || (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} 
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" 
                            />
                        </svg>
                        <p>{emptyMessage}</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={containerClasses}>
            {/* Estilos responsive inline */}
            {responsive && (
                <style>{`
                    @media (min-width: 768px) {
                        .table-responsive { display: table !important; }
                        .cards-responsive { display: none !important; }
                    }
                `}</style>
            )}
            
            {/* Tabla Desktop */}
            <div className="overflow-x-auto">
                <table 
                    className={`${tableClasses} ${responsive ? 'table-responsive' : ''}`}
                    style={responsive ? { display: 'none' } : undefined}
                >
                    {/* Header */}
                    <thead className={headerClasses}>
                        <tr>
                            {allColumns.map((column) => (
                                <th 
                                    key={column.key}
                                    className={`
                                        ${cellClasses} 
                                        font-bold
                                        ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                                        ${column.sortable && sorting ? 'cursor-pointer select-none hover:bg-gray-200' : ''}
                                        ${column.headerClassName || ''}
                                    `}
                                    style={column.width ? { width: column.width } : {}}
                                    onClick={() => {
                                        if (column.sortable && sorting?.onSort) {
                                            sorting.onSort(column.key);
                                        }
                                    }}
                                >
                                    <div className={`flex items-center ${column.align === 'center' ? 'justify-center' : column.align === 'right' ? 'justify-end' : ''}`}>
                                        {column.label}
                                        {column.sortable && sorting && (
                                            <SortIcon field={column.key} sortConfig={sorting} />
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    {/* Body */}
                    <tbody className={`bg-white ${bodyClassName}`}>
                        {data.map((row, rowIndex) => (
                            <tr key={keyExtractor(row)} className={getRowClasses(row, rowIndex)}>
                                {allColumns.map((column) => {
                                    // Columna de acciones
                                    if (column.key === '_actions') {
                                        return (
                                            <td key="_actions" className={`${cellClasses} text-center`}>
                                                <div className="flex items-center justify-center gap-1">
                                                    {actions.map((action, actionIndex) => (
                                                        <ActionButton 
                                                            key={actionIndex} 
                                                            action={action} 
                                                            row={row}
                                                            permissions={permissions}
                                                        />
                                                    ))}
                                                </div>
                                            </td>
                                        );
                                    }

                                    // Obtener valor (soporta campos anidados)
                                    const value = column.key.includes('.') 
                                        ? getNestedValue(row, column.key)
                                        : row[column.key];

                                    // Renderizar celda
                                    return (
                                        <td 
                                            key={column.key}
                                            className={`
                                                ${cellClasses}
                                                ${column.align === 'center' ? 'text-center' : ''}
                                                ${column.align === 'right' ? 'text-right' : ''}
                                                ${column.className || ''}
                                            `}
                                        >
                                            {column.render 
                                                ? column.render(value, row, rowIndex)
                                                : value ?? '-'
                                            }
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Vista móvil responsive (cards) - visible solo en móvil */}
            {responsive && (
                <div className="cards-responsive space-y-3 p-4">
                    {data.map((row, rowIndex) => (
                        <div 
                            key={keyExtractor(row)} 
                            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                        >
                            {/* Header del card con primera columna */}
                            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                                <div className="font-medium text-gray-900">
                                    {columns[0]?.render 
                                        ? columns[0].render(row[columns[0].key], row, rowIndex)
                                        : row[columns[0]?.key] ?? '-'
                                    }
                                </div>
                            </div>

                            {/* Contenido del card */}
                            <div className="p-4 space-y-2">
                                {columns.slice(1).map((column) => {
                                    const value = column.key.includes('.') 
                                        ? getNestedValue(row, column.key)
                                        : row[column.key];

                                    return (
                                        <div key={column.key} className="flex justify-between items-start">
                                            <span className="text-xs font-medium text-gray-500 uppercase">
                                                {column.label}
                                            </span>
                                            <span className="text-sm text-gray-900 text-right">
                                                {column.render 
                                                    ? column.render(value, row, rowIndex)
                                                    : value ?? '-'
                                                }
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Acciones del card */}
                            {actions.length > 0 && (
                                <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
                                    {actions.map((action, actionIndex) => (
                                        <ActionButton 
                                            key={actionIndex} 
                                            action={action} 
                                            row={row}
                                            permissions={permissions}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Paginación */}
            {pagination && (
                <TablePagination
                    currentPage={pagination.page}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    onPageChange={pagination.onPageChange}
                    onItemsPerPageChange={pagination.onItemsPerPageChange}
                    itemsPerPageOptions={pagination.itemsPerPageOptions}
                />
            )}
        </div>
    );
};

export default Table;
